import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Tooltip,
  Chip,
  Button,
  Input,
  CircularProgress,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { useDispatch, useSelector } from "react-redux";
import { useGMBProfile } from "../hook/useGMBProfile";
import { fetchStoreDetails } from "../../../Store/Gmb/gmbStoreSlice";
import {
  selectVerificationOptions,
  fetchVerificationOptions,
} from "../../../Store/Gmb/gmbVerificationSlice";
import GoogleMap from "./Googlemap";
import Dropdown from "./Dropdown";
import ImageView from "./ImageView";
import StoreVerification from "../storeverification/StoreVerification";
import EditableTextArea from "./EditableTextArea";
import { useIsGmbAdmin } from "../hook/useIsGMBAdmin";
import "../style.css";
import PostView from "./Posts.jsx";

// Label text component for reusability
const LabelText = ({ label, text, children, isEditting = false }) => (
  <Box className="flex flex-col items-start justify-between mx-3 my-2">
    <Typography className="text-[#172128] font-normal text-xs mb-1">
      {label}
    </Typography>

    {!isEditting ? (
      text ? (
        text.toLowerCase().includes("mon") ? (
          <Typography className="text-[#172128] font-semibold text-xs w-fit overflow-auto">
            {text}
          </Typography>
        ) : (
          text
            .split(",")
            .slice(0, 5)
            .map((item) => (
              <Typography
                key={item}
                className="text-[#172128] font-semibold text-xs w-fit overflow-auto"
              >
                {item.trim() || "--"}
              </Typography>
            ))
        )
      ) : (
        "--"
      )
    ) : (
      children
    )}
  </Box>
);

// Action buttons component
const ActionButtons = ({
  isEditing,
  loading,
  onEdit,
  onCancel,
  onSubmit,
  hasChanges,
  disabled,
  accountId,
  locationId,
  has_voice_of_merchant,
}) => (
  <div className="flex gap-2">
    {!isEditing ? (
      <>
        <Button
          className="bg-[#ECE6F0] px-4 py-2 rounded-2xl flex items-center capitalize shadow-[0_4px_8px_3px_rgba(0,0,0,0.15)] gap-1"
          onClick={onEdit}
          // disabled={disabled}
        >
          <ModeEditIcon sx={{ color: "#65558F", fontSize: "20px" }} />
          <Typography className="font-medium text-[#65558F]">Edit</Typography>
        </Button>
      </>
    ) : (
      <div className="flex gap-2">
        {isEditing && !has_voice_of_merchant && (
          <StoreVerification
            accountId={accountId}
            locationId={locationId}
            isStoreVerified={has_voice_of_merchant}
          />
        )}
        <Button
          className="bg-[#ECE6F0] px-4 py-2 rounded-2xl flex items-center capitalize shadow-[0_4px_8px_3px_rgba(0,0,0,0.15)]"
          onClick={onCancel}
        >
          <span className="font-medium text-[#65558F]">Cancel</span>
        </Button>
        <Button
          className={`px-4 py-2 rounded-2xl flex items-center capitalize shadow-[0_4px_8px_3px_rgba(0,0,0,0.15)]
            ${
              loading || hasChanges
                ? "bg-gray-200 text-gray-500"
                : "bg-[#EC221F] text-white hover:bg-[#FA4B16]"
            }`}
          onClick={onSubmit}
          disabled={loading || hasChanges}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Submit"}
        </Button>
      </div>
    )}
  </div>
);

export default function GMBProfileDetail() {
  const dispatch = useDispatch();
  const {
    formData,
    loading,
    isSubmitting,
    isEditing,
    hasChanges,
    isStoreStatusPending,
    IS_STORE_CLOSED_PERMANENTLY,
    statusOptions,
    accountNameOptions,
    handleFieldUpdate,
    handleSubmit,
    handleCancel,
    handleEditToggle,
    allStoreAccounts,
    storeDetail,
  } = useGMBProfile();

  const DISABLED_ACCOUNT_MOVE_OPTION = true;
  const isAdmin = useIsGmbAdmin();

  if (!storeDetail) return null;

  const handleSuccessfulSubmit = async () => {
    const success = await handleSubmit();
    if (success) {
      // Refresh store details after successful update
      dispatch(fetchStoreDetails(storeDetail.store_code));
    }
  };

  return (
    <div className="mt-2">
      {/* Header Section */}
      <div className="flex items-center justify-between mx-2 mb-2">
        <Box
          sx={{
            display: "flex",
            // width: "100%",
            maxWidth: "fit-content",
            alignItems: "center",
            gap: { sm: "4px", md: "12px" },
          }}
        >
          {isEditing && (
            <>
              <Typography className="font-normal text-sm lg:text-xl text-[#172128] ">
                Title:
              </Typography>
              <input
                type="text"
                defaultValue={storeDetail?.title}
                className="px-2 py-1 rounded border-slate-300 bg-[#F1F6FA]"
                style={{
                  width: `${storeDetail?.title?.length || 1}ch`, // Dynamically adjust width
                  minWidth: "50px", // Set a reasonable minimum width
                  maxWidth: "100%", // Prevent it from exceeding container width
                }}
                onChange={(e) => {
                  const title = e.target.value;
                  handleFieldUpdate("title", title);
                }}
              />
            </>
          )}
          {!isEditing && (
            <Typography className="font-normal text-sm lg:text-xl text-[#172128] ">
              Title: {storeDetail?.title}
            </Typography>
          )}

          {/*if has_voice_of_merchant is true  */}
          {storeDetail.has_voice_of_merchant && (
            <Tooltip title="Your Google My Business Profile is Verified by Google">
              <VerifiedIcon fontSize="small" sx={{ color: "blue" }} />
            </Tooltip>
          )}
          {/* User has performed verification but the google verification is not complete */}
          {!storeDetail.has_voice_of_merchant &&
            storeDetail.is_verified === "COMPLETED" && (
              <Tooltip title="Your Google Business Profile verification is currently pending. Google typically takes up to 5 business days to complete the verification process.">
                <Chip
                  size="small"
                  label="Verification Pending"
                  color="warning"
                />
              </Tooltip>
            )}
        </Box>

        {isAdmin && (
          <ActionButtons
            isEditing={isEditing}
            loading={isSubmitting}
            onEdit={() => handleEditToggle(true)}
            onCancel={handleCancel}
            onSubmit={handleSuccessfulSubmit}
            disabled={isStoreStatusPending}
            hasChanges={!hasChanges}
            accountId={storeDetail.account_id}
            locationId={storeDetail.location_id}
            has_voice_of_merchant={storeDetail.has_voice_of_merchant}
          />
        )}
      </div>
      {/* Store Details Section */}
      <Box
        spacing={2}
        className="flex flex-wrap justify-between gap-2 max-h-fit sm:flex-nowrap "
      >
        {/* Left Card */}
        <Box className="flex-grow border border-[#F1F6FA] rounded-[20px] p-2">
          <Box className="grid h-full grid-cols-2 gap-4">
            <div>
              <LabelText
                label="status"
                text={formData.storeStatus?.replace("_", " ")}
                isEditting={!isStoreStatusPending && isEditing}
              >
                <Dropdown
                  defaultValue={formData.storeStatus}
                  onSelectedValue={(value) =>
                    handleFieldUpdate("storeStatus", value)
                  }
                  optionList={statusOptions}
                />
              </LabelText>

              <LabelText label="city" text={storeDetail.dc_city} />

              <LabelText
                label="labels"
                text={formData.storeLabels}
                isEditting={
                  !IS_STORE_CLOSED_PERMANENTLY &&
                  !isStoreStatusPending &&
                  isEditing
                }
              >
                <EditableTextArea
                  onUpdate={(value) => handleFieldUpdate("storeLabels", value)}
                  initialData={formData.storeLabels}
                />
              </LabelText>
            </div>

            <GoogleMap
              lat={storeDetail.latitude}
              long={storeDetail.longitude}
              map_url={storeDetail.maps_uri}
            />
          </Box>
        </Box>

        {/* Right Card */}
        <Box className="w-full sm:w-[45%] border border-[#F1F6FA] rounded-[20px] p-2">
          <Box className="grid grid-cols-2 gap-4">
            <div>
              <LabelText
                label="account name"
                text={formData.currentAccountName}
                isEditting={
                  !IS_STORE_CLOSED_PERMANENTLY &&
                  !isStoreStatusPending &&
                  isEditing &&
                  !DISABLED_ACCOUNT_MOVE_OPTION
                }
              >
                <Dropdown
                  defaultValue={formData.currentAccountName}
                  onSelectedValue={(value) =>
                    handleFieldUpdate("currentAccountName", value)
                  }
                  optionList={accountNameOptions}
                />
              </LabelText>
              <LabelText
                label="operating hours"
                text={`Mon - Sun, ${formData.operatingHours?.openingTime} - ${formData.operatingHours?.closingTime}`}
                isEditting={
                  !IS_STORE_CLOSED_PERMANENTLY &&
                  !isStoreStatusPending &&
                  isEditing
                }
              >
                <Box>
                  <Typography className="text-xs font-semibold">
                    Mon - Sun
                  </Typography>
                  <Box className="flex flex-wrap gap-2 max-w-fit">
                    <input
                      type="time"
                      className="bg-[#F1F6FA] text-black pl-2 text-xs rounded-md h-6 w-[80px] border-none"
                      value={formData.operatingHours?.openingTime || "00:00"}
                      onChange={(e) => {
                        const value = e.target.value;
                        const closingTime =
                          formData.operatingHours?.closingTime;
                        handleFieldUpdate("operatingHours", {
                          openingTime: value,
                          closingTime,
                        });
                      }}
                    />
                    <input
                      type="time"
                      className="bg-[#F1F6FA] text-black pl-2 text-xs rounded-md h-6 w-[80px] border-none"
                      value={formData.operatingHours?.closingTime || "00:00"}
                      onChange={(e) => {
                        const value = e.target.value;
                        const openingTime =
                          formData.operatingHours?.openingTime;

                        handleFieldUpdate("operatingHours", {
                          openingTime,
                          closingTime: value,
                        });
                      }}
                    />
                  </Box>
                </Box>
              </LabelText>

              <LabelText
                label="phone number"
                text={formData.phoneNumber}
                isEditting={
                  !IS_STORE_CLOSED_PERMANENTLY &&
                  !isStoreStatusPending &&
                  isEditing
                }
              >
                <input
                  type="tel"
                  className="bg-[#F1F6FA] text-black pl-2 text-xs rounded-md h-6 w-[100px] border-none"
                  maxLength={10}
                  value={formData.phoneNumber}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleFieldUpdate("phoneNumber", value);
                  }}
                  placeholder="Enter number"
                />
              </LabelText>

              <LabelText label="created date" text={storeDetail.insert_time} />
              <LabelText
                label="last modified date"
                text={storeDetail.update_time}
              />
            </div>

            {/* Stats Section */}
            <div className="flex flex-col justify-center gap-4">
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: {
                      xs: "14px", // Extra-small screens and up
                      sm: "14px", // Small screens and up
                      md: "14px", // Medium screens and down
                      lg: "20px", // Larger screens use the default size for variant="h6"
                    },
                  }}
                >
                  Average Rating
                </Typography>
                <Typography
                  variant="h6"
                  color={
                    Number(storeDetail.average_rating)?.toFixed(1) < 4.0
                      ? "red"
                      : "green"
                  }
                  sx={{
                    fontSize: {
                      xs: "14px", // Extra-small screens and up
                      sm: "14px", // Small screens and up
                      md: "14px", // Medium screens and down
                      lg: "20px", // Larger screens use the default size for variant="h6"
                    },
                  }}
                >
                  {storeDetail.average_rating?.toFixed(1) ?? "--"}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: {
                      xs: "14px", // Extra-small screens and up
                      sm: "14px", // Small screens and up
                      md: "14px", // Medium screens and down
                      lg: "20px", // Larger screens use the default size for variant="h6"
                    },
                  }}
                >
                  Review Count
                </Typography>
                <Typography
                  variant="h6"
                  color="red"
                  sx={{
                    fontSize: {
                      xs: "14px", // Extra-small screens and up
                      sm: "14px", // Small screens and up
                      md: "14px", // Medium screens and down
                      lg: "20px", // Larger screens use the default size for variant="h6"
                    },
                  }}
                >
                  {storeDetail.total_review_count ?? "--"}
                </Typography>
              </Box>
            </div>
          </Box>
        </Box>
      </Box>
      {/* Image View Section */}
      <ImageView
        accountId={storeDetail.account_id}
        locationId={storeDetail.location_id}
        disabled={isStoreStatusPending || isEditing}
      />

      <PostView
        accountId={storeDetail.account_id}
        locationId={storeDetail.location_id}
        disabled={isStoreStatusPending || isEditing}
      />
    </div>
  );
}
